// Mini Leagues: every friends-only league layered over a base league. Searchable
// by name, base league, owner or invite code. Read-only — vanity standings live
// inside the app; here it's just the roster of who created what.
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { MiniLeagueMark } from '../../components/ui/bits';
import { EmptyState, PageTitle, formatDate, useAdminData } from './parts';

export default function MiniLeagues() {
  const { data } = useAdminData();
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.miniLeagues;
    return data.miniLeagues.filter(
      (m) =>
        m.name.toLowerCase().includes(needle) ||
        m.leagueName.toLowerCase().includes(needle) ||
        m.ownerName.toLowerCase().includes(needle) ||
        m.inviteCode.toLowerCase().includes(needle)
    );
  }, [data.miniLeagues, q]);

  return (
    <>
      <PageTitle title="Mini Leagues" sub={`${data.miniLeagues.length} friend leagues`} />

      <div className="mb-5 flex items-center gap-2 rounded-2xl border border-black/10 bg-card px-3.5 py-2.5 shadow-tile">
        <Search size={16} className="text-ink3" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, league, owner or code"
          className="w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-ink3"
        />
      </div>

      {list.length === 0 ? (
        <EmptyState>{data.miniLeagues.length === 0 ? 'No mini leagues yet.' : 'No matches.'}</EmptyState>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((m) => (
            <div
              key={m.miniLeagueId}
              className="flex items-center gap-3 rounded-2xl border border-black/5 bg-card p-4 shadow-tile"
            >
              <MiniLeagueMark icon={m.icon} color={m.color} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-extrabold tracking-tight">{m.name}</div>
                <div className="truncate text-[12px] text-ink2">
                  {m.leagueName} · {m.ownerName}
                </div>
                <div className="mt-0.5 text-[11px] text-ink3">Created {formatDate(m.createdAt)}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="num text-[18px] font-extrabold leading-none">{m.memberCount}</div>
                <div className="text-[11px] text-ink3">members</div>
                <div className="num mt-1 rounded-md bg-fill px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-ink2">
                  {m.inviteCode}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
